import { getMaxDigitWithDot } from '@trader/utils';

import {
  updateObjByDotNotation,
  getMarketValueVolume,
  getProtectionValue,
  getProtectionValueRange,
  getMarketReq,
  getAmount,
  calculateIndicesPips,
} from '../utils';
import { IInstrument, IRealPrice } from '../types';
import { IUseEditingTradingFieldsOutput } from './useEditingTradingFields';
import { IGetInstrumentOutput } from './useGetInstrument';
import { IUseTradingValidationsOutput } from './useTradingValidations';
import {
  calculateProfitPips,
  calculateStopLossPips,
} from '../utils/pipsCalculating';

export const useTradingMethods = (
  instrument: IInstrument,
  realTimePrice: IRealPrice,
  toggleEditField: IUseEditingTradingFieldsOutput['toggleEditField'],
  updateInstrument: IGetInstrumentOutput['updateInstrument'],
  removeProtectionErrors: IUseTradingValidationsOutput['removeProtectionErrors'],
  clearEditingFields: IUseEditingTradingFieldsOutput['clearEditingFields'],
  removeSomeError: IUseTradingValidationsOutput['removeSomeError']
) => {
  const handleChangeMarket = (value, name, variation) => {
    if (name === 'amountVolume') {
      toggleEditField('marketAmount');
      updateInstrument(prev => ({
        ...prev,
        market: {
          ...prev.market,
          amountVolume: value,
          valueVolume: getMarketValueVolume(
            prev.market.billType,
            +realTimePrice.ask,
            +realTimePrice.bid,
            value,
            prev.general.pipSize
          ),
          reqMarginValue: getMarketReq(
            realTimePrice.ask,
            realTimePrice.bid,
            prev.general.leverage,
            Number(value)
          ),
        },
      }));
    } else {
      if (
        Number(instrument.market.amountVolume) >= 0 ||
        variation !== 'minus'
      ) {
        const amount = getAmount(
          instrument.market.amountVolume,
          instrument.general.fixDigitAfterDot,
          variation,
          instrument.general.step
        );
        updateInstrument(prev => ({
          ...prev,
          market: {
            ...prev.market,
            amountVolume: amount,
            valueVolume: getMarketValueVolume(
              prev.market.billType,
              +realTimePrice.ask,
              +realTimePrice.bid,
              +amount,
              prev.general.pipSize
            ),
            reqMarginValue: getMarketReq(
              realTimePrice.ask,
              realTimePrice.bid,
              prev.general.leverage,
              Number(amount)
            ),
          },
        }));
      }
    }
  };

  const handleChangeProtection = (value, name) => {
    if (name === 'profitValue') {
      updateInstrument(prev =>
        updateObjByDotNotation(prev, 'market.protection.profitValue', value)
      );
      toggleEditField('takeProfit');
      const takeProfitPips =
        instrument.market.billType === 'Buy'
          ? +calculateIndicesPips(
              (Number(value) - Number(realTimePrice.ask)) /
                instrument.general.pipSize,
              'toPips'
            )
          : +calculateIndicesPips(
              (Number(realTimePrice.bid) - Number(value)) /
                instrument.general.pipSize,
              'toPips'
            );
      updateInstrument(prev =>
        updateObjByDotNotation(
          prev,
          'market.protection.profitPips',
          takeProfitPips.toFixed(0)
        )
      );
    } else {
      updateInstrument(prev =>
        updateObjByDotNotation(prev, 'market.protection.lossValue', value)
      );
      toggleEditField('stopLoss');
      const stopLossPips =
        instrument.market.billType === 'Buy'
          ? +calculateIndicesPips(
              (Number(realTimePrice.ask) - Number(value)) /
                instrument.general.pipSize,
              'toPips'
            )
          : +calculateIndicesPips(
              (Number(value) - Number(realTimePrice.bid)) /
                instrument.general.pipSize,
              'toPips'
            );

      updateInstrument(prev =>
        updateObjByDotNotation(
          prev,
          'market.protection.stopLossPips',
          stopLossPips.toFixed(0)
        )
      );
    }
  };

  const handleChangeUnit = (value, name) => {
    updateInstrument(prev =>
      updateObjByDotNotation(prev, `market.protection.${name}`, value)
    );
  };

  const handleChangePips = (value, name) => {
    updateInstrument(prev =>
      updateObjByDotNotation(prev, `market.protection.${name}`, value)
    );

    if (name === 'profitPips') {
      toggleEditField('takeProfit');

      const takeProfitValue = calculateProfitPips({
        ask: realTimePrice.ask,
        bid: realTimePrice.bid,
        side: instrument.market.billType,
        pipSize: instrument.general.pipSize,
        stopsLevel: value,
        fixDigit: getMaxDigitWithDot(instrument.general.pipSize),
      });
      updateInstrument(prev =>
        updateObjByDotNotation(
          prev,
          'market.protection.profitValue',
          takeProfitValue
        )
      );
    }

    if (name === 'stopLossPips') {
      toggleEditField('stopLoss');
      const stopLossValue = calculateStopLossPips({
        ask: realTimePrice.ask,
        bid: realTimePrice.bid,
        side: instrument.market.billType,
        pipSize: instrument.general.pipSize,
        stopsLevel: value,
        fixDigit: getMaxDigitWithDot(instrument.general.pipSize),
      });
      updateInstrument(prev =>
        updateObjByDotNotation(
          prev,
          'market.protection.lossValue',
          stopLossValue
        )
      );
    }
  };

  const handleChangeStopLimit = (value, name, errorName, variation) => {
    switch (name) {
      case 'amount':
        toggleEditField('limitAmount');
        updateInstrument(prev => ({
          ...prev,
          limit: {
            ...prev.limit,
            amount: value,
            value: getMarketValueVolume(
              prev.limit.billType,
              prev.limit.sellValue,
              prev.limit.buyValue,
              value,
              prev.general.pipSize
            ),
            reqMargin: getMarketReq(
              prev.limit.sellValue,
              prev.limit.buyValue,
              prev.general.leverage,
              Number(value)
            ),
          },
        }));
        break;
      case 'value':
        if (Number(instrument.limit.amount) > 0 || variation !== 'minus') {
          const nAmount = getAmount(
            instrument.limit.amount,
            instrument.general.fixDigitAfterDot,
            variation,
            instrument.general.step
          );
          updateInstrument(prev => ({
            ...prev,
            limit: {
              ...prev.limit,
              amount: nAmount,
              value: getMarketValueVolume(
                prev.limit.billType,
                prev.limit.sellValue,
                prev.limit.buyValue,
                +nAmount,
                prev.general.pipSize
              ),
              reqMargin: getMarketReq(
                prev.limit.sellValue,
                prev.limit.buyValue,
                prev.general.leverage,
                Number(nAmount)
              ),
            },
          }));
        }
        break;
      case 'buyValue':
        toggleEditField('limitBuy');
        updateInstrument(prev => ({
          ...prev,
          limit: {
            ...prev.limit,
            buyValue: value,
            reqMargin: getMarketReq(
              prev.limit.sellValue,
              value,
              prev.general.leverage,
              Number(prev.limit.amount)
            ),
          },
        }));
        break;
      case 'sellValue':
        toggleEditField('limitSell');
        updateInstrument(prev => ({
          ...prev,
          limit: {
            ...prev.limit,
            sellValue: value,
            reqMargin: getMarketReq(
              value,
              prev.limit.buyValue,
              prev.general.leverage,
              Number(prev.limit.amount)
            ),
          },
        }));
        break;
      default:
        console.log('smth goes wrong');
    }
    // handleSomeError(value, errorName, instrument.market.protection.minValue, Infinity);
  };

  const handleMarketBillType = type => {
    updateInstrument(prev => ({
      ...prev,
      market: {
        ...prev.market,
        billType: type,
        valueVolume: getMarketValueVolume(
          type,
          +realTimePrice.ask,
          +realTimePrice.bid,
          prev.market.amountVolume,
          prev.general.pipSize
        ),
      },
    }));
  };

  const handleLimitBillType = type => {
    updateInstrument(prev => ({
      ...prev,
      limit: {
        ...prev.limit,
        billType: type,
        value: getMarketValueVolume(
          type,
          +realTimePrice.ask,
          +realTimePrice.bid,
          prev.limit.amount,
          prev.general.pipSize
        ),
      },
    }));
  };

  const handleClearProtection = () => {
    const maxDigit = getMaxDigitWithDot(instrument.general.pipSize);
    const protectionValue = getProtectionValue(
      instrument.market.billType,
      realTimePrice.ask,
      realTimePrice.bid,
      maxDigit,
      instrument.general.pipSize,
      instrument.market.protection.stopsLevel
    );
    const protectionRange = getProtectionValueRange(
      instrument.market.billType,
      realTimePrice.ask,
      instrument.general.step,
      realTimePrice.bid
    );

    updateInstrument(prev => ({
      ...prev,
      market: {
        ...prev.market,
        protection: {
          lossValue: protectionValue.loss,
          profitValue: protectionValue.profit,
          minValue: protectionRange.min,
          maxValue: protectionRange.max,
          isSwitchLoss: false,
          isSwitchProfit: false,
        },
      },
    }));
    removeProtectionErrors();
    clearEditingFields();
  };

  const switchTakeProfit = value => {
    updateInstrument(prev =>
      updateObjByDotNotation(prev, 'market.protection.isSwitchProfit', value)
    );
    if (!value) {
      toggleEditField('takeProfit', false);
      removeSomeError('takeProfitError');
    }
  };

  const switchStopLoss = value => {
    updateInstrument(prev =>
      updateObjByDotNotation(prev, 'market.protection.isSwitchLoss', value)
    );
    if (!value) {
      toggleEditField('stopLoss', false);
      removeSomeError('stopLossError');
    }
  };

  return {
    handleChangeMarket,
    handleChangeProtection,
    handleChangeStopLimit,
    handleMarketBillType,
    handleLimitBillType,
    handleClearProtection,
    switchTakeProfit,
    switchStopLoss,
    handleChangeUnit,
    handleChangePips,
  };
};
